import { el } from '../utils/dom.js';

/**
 * Welcome screen — first thing users see.
 * Shows value prop, quick actions, and account pitch.
 */
export class WelcomeScreen {
  constructor(state, fileManager, tilesetLoader, newMapDialog, generatorPanel, mapCanvas, compositor, auth, authUI) {
    this.state = state;
    this.fileManager = fileManager;
    this.tilesetLoader = tilesetLoader;
    this.newMapDialog = newMapDialog;
    this.generatorPanel = generatorPanel;
    this.mapCanvas = mapCanvas;
    this.compositor = compositor;
    this.auth = auth || null;
    this.authUI = authUI || null;
  }

  show() {
    const isSignedIn = this.auth?.isAuthenticated();
    const overlay = el('div', { className: 'welcome-overlay' });
    const card = el('div', { className: 'welcome-card' });

    // Header
    const header = el('div', { className: 'welcome-header' });
    header.appendChild(el('img', { src: '/echoforge-logo.png', className: 'welcome-logo' }));
    header.appendChild(el('h1', { className: 'welcome-title' }, 'TileForge Studio'));
    header.appendChild(el('p', { className: 'welcome-subtitle' }, 'The browser-based 2D map editor for game developers'));
    card.appendChild(header);

    // Feature highlights (first-time value prop)
    const features = el('div', { className: 'welcome-features' });
    features.innerHTML = `
      <div class="welcome-feature">
        <span class="welcome-feature-icon">🗺️</span>
        <div><strong>Tiled-Compatible</strong><br><span>Import/export TMJ & TMX. Works with Godot, Phaser, Unity.</span></div>
      </div>
      <div class="welcome-feature">
        <span class="welcome-feature-icon">⚡</span>
        <div><strong>Procedural Generation</strong><br><span>6 built-in generators — forests, dungeons, caves, villages & more.</span></div>
      </div>
      <div class="welcome-feature">
        <span class="welcome-feature-icon">🎨</span>
        <div><strong>Full Editor Suite</strong><br><span>Pencil, fill, rectangle, select, copy/paste, undo/redo, layers.</span></div>
      </div>
    `;
    card.appendChild(features);

    // Quick actions
    const actions = el('div', { className: 'welcome-actions' });
    actions.appendChild(this._actionButton('✨ New Map', 'Choose engine, tile size, and dimensions', () => { overlay.remove(); this.newMapDialog.show(); }));
    actions.appendChild(this._actionButton('📂 Open File', 'Load a TMJ or TMX map file', () => { overlay.remove(); this.fileManager.openFile(); }));
    actions.appendChild(this._actionButton('🎲 Generate', 'Procedurally generate a map', () => { overlay.remove(); this.generatorPanel.show(); }));
    actions.appendChild(this._actionButton('▶ Quick Start', 'Jump in with a blank 40×30 map', () => overlay.remove()));
    card.appendChild(actions);

    // Account section — the pitch
    if (!isSignedIn) {
      const accountSection = el('div', { className: 'welcome-account' });
      accountSection.innerHTML = `
        <div class="welcome-account-header">
          <strong>Create a free EchoForge account</strong>
        </div>
        <div class="welcome-account-perks">
          <div class="welcome-perk">☁️ 25 MB cloud storage — access your maps from anywhere</div>
          <div class="welcome-perk">🔗 Save directly to GitHub repos</div>
          <div class="welcome-perk">🎨 Generate tilesets with PixelLab AI</div>
          <div class="welcome-perk">💾 Auto-save & sync across devices</div>
        </div>
      `;

      const accountBtns = el('div', { className: 'welcome-account-buttons' });
      const signUpBtn = el('button', {
        className: 'btn btn-gold',
        onClick: () => {
          overlay.remove();
          if (this.authUI) this.authUI._showAuthDialog('signup');
        },
      }, 'Create Free Account');

      const signInBtn = el('button', {
        className: 'btn btn-outline',
        onClick: () => {
          overlay.remove();
          if (this.authUI) this.authUI._showAuthDialog('signin');
        },
      }, 'Sign In');

      const skipBtn = el('button', {
        className: 'btn btn-text',
        onClick: () => overlay.remove(),
      }, 'Continue without account');

      accountBtns.append(signUpBtn, signInBtn);
      accountSection.append(accountBtns);
      accountSection.appendChild(skipBtn);
      card.appendChild(accountSection);
    } else {
      // Signed in — show welcome back
      const user = this.auth.getUser();
      const welcomeBack = el('div', { className: 'welcome-account welcome-back' });
      const header = el('div', { className: 'welcome-account-header' });
      const greeting = el('strong');
      greeting.textContent = `Welcome back, ${user.displayName || user.email}`;
      header.appendChild(greeting);
      welcomeBack.appendChild(header);
      const perk = el('div', { className: 'welcome-perk' });
      perk.textContent = '☁️ Cloud storage connected — your maps sync automatically';
      welcomeBack.appendChild(perk);
      card.appendChild(welcomeBack);
    }

    // Keyboard hints
    const hints = el('div', { className: 'welcome-hints' });
    hints.innerHTML = `
      <div class="welcome-hint"><kbd>B</kbd> Pencil <kbd>R</kbd> Rect <kbd>G</kbd> Fill <kbd>E</kbd> Eraser <kbd>S</kbd> Select</div>
      <div class="welcome-hint"><kbd>Ctrl+S</kbd> Save <kbd>Ctrl+Z</kbd> Undo <kbd>Scroll</kbd> Zoom <kbd>Middle-click</kbd> Pan</div>
    `;
    card.appendChild(hints);

    // Footer
    const footer = el('div', { className: 'welcome-footer' });
    footer.innerHTML = `
      <a href="https://echoforge.games/tileforge" target="_blank">About</a>
      &nbsp;·&nbsp;
      <a href="https://echoforge.games/terms" target="_blank">Terms</a>
      &nbsp;·&nbsp;
      <a href="https://echoforge.games/privacy" target="_blank">Privacy</a>
      &nbsp;·&nbsp;
      <a href="https://discord.gg/x4VBCU4V" target="_blank">Discord</a>
      &nbsp;·&nbsp;
      v1.0
    `;
    card.appendChild(footer);

    overlay.appendChild(card);
    document.body.appendChild(overlay);

    const onKey = (e) => {
      if (e.key === 'Escape') { overlay.remove(); document.removeEventListener('keydown', onKey); }
    };
    document.addEventListener('keydown', onKey);
  }

  _actionButton(title, desc, action) {
    const btn = el('button', { className: 'welcome-action', onClick: action });
    btn.appendChild(el('div', { className: 'welcome-action-title' }, title));
    btn.appendChild(el('div', { className: 'welcome-action-desc' }, desc));
    return btn;
  }
}
